import { LOCAL_ERROR_FEEDBACK } from './local-error-feedback'
import type { Observable } from 'rxjs'
import {
  throwError,
  of,
  timeoutWith,
  finalize,
  mergeMap,
  delay,
  switchMap,
  forkJoin,
  map,
  timer,
  Subject,
  catchError,
  defer,
  tap,
  EMPTY,
  filter,
  takeUntil
} from 'rxjs'
import type { HttpParameterCodec, HttpContext } from '@angular/common/http'
import { HttpClient, HttpParams, HttpResponse, HttpHeaders, HttpErrorResponse, HttpHandler } from '@angular/common/http'
import { EventEmitter, Injectable, inject, effect } from '@angular/core'
import isTest from '@awork/_shared/functions/is-test'
import { divideArray, uniqueBy, uniqueByPredicate } from '@awork/_shared/functions/array-operations'
import { ApiCacheService } from '@awork/_shared/services/api-cache-service/api-cache.service'
import type {
  AnyApiCacheInvalidationOptions,
  ApiCacheOptions,
  EntityKey
} from '@awork/_shared/services/api-cache-service/types'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { app } from '@awork/environments/environment'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { toObservable } from '@angular/core/rxjs-interop'

interface RetryConfig {
  maxRetryAttempts?: number
  scalingDuration?: number
  excludedStatusCodes?: number[]
}

interface RetryState {
  workspaceId?: string
  url: string
  options?: Options
  requestTimeout: number
  shouldApplyTimeout: boolean
  deadlineAt?: number
  attempt: number
  lastErrorStatus?: number
}

export interface TraceSignalExclusion {
  entityName: string
  propertyName: string
}

interface PendingSignalTrace {
  expiryTimeout?: number
}

interface SignalTraceContext {
  traceId: string
  traceParent: string
}

/**
 * A custom encoder used for HtpParams to prevent bad encoding of the '+' sign
 */
class CustomEncoder implements HttpParameterCodec {
  /**
   * Encodes a query parameter key.
   * @param {string} key - Query parameter text.
   * @returns {string} Converted query parameter text.
   */
  encodeKey(key: string): string {
    return encodeURIComponent(key)
  }

  /**
   * Encodes a query parameter value.
   * @param {string} value - Query parameter text.
   * @returns {string} Converted query parameter text.
   */
  encodeValue(value: string): string {
    return encodeURIComponent(value)
  }

  /**
   * Decodes a query parameter key.
   * @param {string} key - Query parameter text.
   * @returns {string} Converted query parameter text.
   */
  decodeKey(key: string): string {
    return decodeURIComponent(key)
  }

  /**
   * Decodes a query parameter value.
   * @param {string} value - Query parameter text.
   * @returns {string} Converted query parameter text.
   */
  decodeValue(value: string): string {
    return decodeURIComponent(value)
  }
}

export interface ErrorResponse {
  code: string
  description: string | string[]
}

export interface MultiStatusResponse {
  badRequest: { ids: string[] }
  notFound: { ids: string[] }
  unauthorized: { ids: string[] }
  ok: { ids: string[] }
}

export interface Options {
  headers?: HttpHeaders | Record<string, string | string[]>
  context?: HttpContext
  observe?: 'body' | 'events' | 'response'
  params?: HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>
  entityName?: string
  propertyName?: string
  reportUploadProgress?: boolean
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer'
  withCredentials?: boolean
  timeout?: number
  cache?: RequestCache
  invalidateCache?: AnyApiCacheInvalidationOptions
  generateTraceId?: boolean
}

export interface GetWithCacheOptions<
  TEntity extends object,
  TKey extends EntityKey<TEntity> = EntityKey<TEntity>
> extends Options {
  apiCache?: ApiCacheOptions<TEntity, TKey>
}

/**
 * Wrapper of the HttpClient to modify the requests globally
 */
@Injectable({ providedIn: 'root' })
export class ApiClient extends HttpClient {
  private readonly workspaceQuery = inject(WorkspaceQuery)
  private readonly workspace = this.workspaceQuery.queryCurrentWorkspace()
  private readonly workspaceChanges = toObservable(this.workspace)
  private apiCacheService = inject(ApiCacheService)
  private featureFlagQuery = inject(FeatureFlagQuery)
  private wasApiCacheEnabled?: boolean

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private req: Observable<any>
  private url: string
  private readonly retries = 3
  private readonly syncRetries = 7
  private readonly delay = 1000
  private readonly maxSyncRetryDelay = 30000
  private readonly syncRetryTimeBudget = 120000
  private readonly timeout = 30000
  private readonly signalTraceExpiryGrace = 1000
  private readonly maxTraceSignalExclusions = 100
  private readonly maxPendingSignalTraces = 100
  private readonly traceSignalExclusionsByTraceId = new Map<string, TraceSignalExclusion>()
  private readonly pendingSignalTraces = new Map<string, PendingSignalTrace>()
  private readonly signalTraceSettledSubject = new Subject<string>()
  readonly maxQueryParamsLength = 1500

  readonly responseStart = new EventEmitter<boolean>()
  readonly responseEnd = new EventEmitter<boolean>()
  readonly signalTraceSettled$ = this.signalTraceSettledSubject.asObservable()

  clientError$ = new Subject<string>()

  isTest = isTest()

  private readonly isApiCacheEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.archApiCache)

  constructor() {
    super(inject(HttpHandler))

    effect(() => this.handleApiCacheFeatureFlagChange(this.isApiCacheEnabled()))
  }

  /**
   * Builds query parameters for collection requests.
   * @param {QueryParams} queryParams - Filters, ordering and paging options.
   * @returns {HttpParams} Serialized collection parameters.
   */
  static getQueryParams(queryParams: QueryParams): HttpParams {
    let params = new HttpParams({ encoder: new CustomEncoder() })

    if (queryParams) {
      if (queryParams.page) {
        params = params.append('page', queryParams.page.toString())
      }

      if (queryParams.pageSize) {
        params = params.append('pageSize', queryParams.pageSize.toString())
      }

      // now supports multi clauses: 'Rating asc,Category/Name desc'
      if (queryParams.orderBy) {
        params = params.append('orderby', queryParams.orderBy.replace(/(?:\r\n|\r|\n)/g, ' '))
      }

      if (queryParams.filterBy) {
        // encode ampersand `&`
        queryParams.filterBy = queryParams.filterBy.replace(/&/g, '%26')
        params = params.append('filterby', queryParams.filterBy.replace(/(?:\r\n|\r|\n)/g, ' '))
      }

      // set this param to true to get the count of the query
      if (queryParams.count) {
        params = params.append('count', queryParams.count ? 'true' : 'false')
      }

      if (queryParams.filterByC) {
        params = params.append('filterbyc', queryParams.filterByC)
      }

      if (queryParams.select) {
        let selectValue = queryParams.select

        if (!selectValue.toLowerCase().includes('createdon')) {
          selectValue = `${selectValue},createdOn`
        }

        params = params.append('select', selectValue)
      }

      if (queryParams.showArchived) {
        params = params.append('showArchived', 'true')
      }
    }

    return params
  }

  /**
   * Stores signal exclusion data for a response trace id so matching websocket signals can be excluded.
   * @param {string} traceId
   * @param {TraceSignalExclusion} exclusion
   */
  saveSignalExclusionForTraceId(traceId: string, exclusion: TraceSignalExclusion): void {
    this.traceSignalExclusionsByTraceId.set(traceId, exclusion)

    if (this.traceSignalExclusionsByTraceId.size > this.maxTraceSignalExclusions) {
      this.traceSignalExclusionsByTraceId.delete(this.traceSignalExclusionsByTraceId.keys().next().value)
    }
  }

  /**
   * Gets the signal exclusion data registered for a response trace id.
   * @param {string} traceId
   * @returns {TraceSignalExclusion | undefined}
   */
  getSignalExclusionForTraceId(traceId: string): TraceSignalExclusion | undefined {
    return this.traceSignalExclusionsByTraceId.get(traceId)
  }

  /**
   * Checks whether an opted-in mutation with the trace id is still pending.
   * @param {string} traceId
   * @returns {boolean}
   */
  isSignalTracePending(traceId?: string): boolean {
    return !!traceId && this.pendingSignalTraces.has(traceId.toLowerCase())
  }

  /**
   * Discards request trace state when the authenticated session changes.
   */
  clearSignalTraceState(): void {
    this.pendingSignalTraces.forEach(({ expiryTimeout }) => {
      if (expiryTimeout !== undefined) {
        clearTimeout(expiryTimeout)
      }
    })
    this.pendingSignalTraces.clear()
    this.traceSignalExclusionsByTraceId.clear()
  }

  /**
   * Clears cached API responses when request caching is disabled at runtime.
   * @param {boolean} isEnabled
   */
  private handleApiCacheFeatureFlagChange(isEnabled: boolean): void {
    const wasEnabled = this.wasApiCacheEnabled

    this.wasApiCacheEnabled = isEnabled

    if (wasEnabled && !isEnabled) {
      this.apiCacheService.invalidate({ all: true })
    }
  }

  /**
   * Sends a GET request to the API
   * @param {string} url
   * @param {Options | GetWithCacheOptions<TEntity, TKey>} options
   * @returns {Observable<T>}
   */
  get<T, TEntity extends object = object, TKey extends EntityKey<TEntity> = EntityKey<TEntity>>(
    url: string,
    options?: Options | GetWithCacheOptions<TEntity, TKey>
  ): Observable<T> {
    const request = () => this.createGetRequest<T>(url, options)
    const apiCache = options && 'apiCache' in options ? options.apiCache : undefined

    if (!apiCache || !this.isApiCacheEnabled()) {
      return request()
    }

    return this.apiCacheService.getOrCreate({
      cache: apiCache,
      keyInput: {
        method: 'GET',
        url,
        options
      },
      request
    })
  }

  /**
   * Creates a GET request Observable with ApiClient modifiers applied.
   * @param {string} url
   * @param {Options} options
   * @returns {Observable<T>}
   */
  private createGetRequest<T>(url: string, options?: Options): Observable<T> {
    this.responseStart.emit(true)

    this.req = super.get<T>(url, this.getHttpOptions(options) as object).pipe(
      tap(response => this.saveSignalExclusionFromResponseTraceId(response, options)),
      map(response => this.unwrapTraceResponse<T>(response, options)),
      finalize(() => {
        this.responseEnd.emit(true)
      })
    )

    this.url = url

    if (this.shouldSkipRetryPolicy()) {
      this.timeoutPolicy(options?.timeout)
    } else {
      this.retryPolicy(options)
    }

    return this.req as Observable<T>
  }

  /**
   * Send GET requests to the API to fetch all entities
   * If multiple pages are available, parallel calls are made to fetch them all
   * @param {string} url
   * @param {Options} [options={ params: ApiClient.getQueryParams({ pageSize: 1000 }) }]
   * @returns {Observable<T>}
   */
  getAll<T>(url: string, options: Options = { params: ApiClient.getQueryParams({ pageSize: 1000 }) }): Observable<T> {
    return this.get<HttpResponse<T[]>>(url, { ...options, observe: 'response' }).pipe(
      switchMap(response => {
        const entities = response.body || []

        const totalItems = Number(response.headers.get('aw-totalitems'))
        const pageSize: number = parseInt((options.params as HttpParams).get('pageSize'))
        const totalPages = Math.ceil(totalItems / pageSize)

        if (totalPages > 1 && (options.params as HttpParams).get('count') !== 'true') {
          const requests: Observable<HttpResponse<T[]>>[] = []

          for (let i = 2; i <= totalPages; i++) {
            options.params = (options.params as HttpParams).set('page', i)
            requests.push(this.get<HttpResponse<T[]>>(url, { ...options, observe: 'response' }))
          }

          return forkJoin(requests).pipe(
            map(results => {
              results.forEach(result => {
                entities.push(...result.body)
              })

              return options.observe === 'response' ? new HttpResponse({ ...response, body: entities }) : entities
            })
          )
        } else {
          return of(options.observe === 'response' ? new HttpResponse({ ...response, body: entities }) : entities)
        }
      })
    ) as unknown as Observable<T>
  }

  /**
   * Sends GET requests to the API batching the query parameters.
   * If the query params are too long (> 1500 characters), multiple calls are made in parallel to send them all.
   * @param {string[]} queryParamValues The values to be sent in the query parameters
   * @param {(values: string[]) => QueryParams | HttpParams} queryParamsFn The function to generate the query parameters
   * @param {(queryParams: QueryParams & HttpParams) => Observable<T[]>} callFn The function to call the API
   * @param {((a: T, b: T) => boolean) | keyof T} equalFnOrProp The function or property to get unique entities
   * @returns {Observable<T[]>} The response of the API call(s)
   * @example
   * getEntitiesById(ids: string[]): Observable<Entity[]> {
   *   const queryParamsFn = (ids: string[]) => ({ filterBy: ids.map(id => `id eq guid'${id}'`).join(' or ') })
   *   const callFn = (queryParams: QueryParams) => this.fetchEntities(queryParams)
   *   return this.getBatch(ids, queryParamsFn, callFn)
   * }
   */
  getBatch<T>(
    queryParamValues: string[],
    queryParamsFn: (values: string[]) => QueryParams | HttpParams,
    callFn: (queryParams: QueryParams & HttpParams) => Observable<T[]>,
    equalFnOrProp?: ((a: T, b: T) => boolean) | keyof T
  ): Observable<T[]> {
    const nBatches = this.getQueryParamMinBatches(queryParamValues, queryParamsFn)
    const valueChunks = divideArray(queryParamValues, nBatches)

    const calls$ = valueChunks?.length
      ? valueChunks.map(valueChunk => {
          const queryParams = queryParamsFn(valueChunk)
          return callFn(queryParams as QueryParams & HttpParams)
        })
      : [callFn({} as QueryParams & HttpParams)]

    return forkJoin(calls$).pipe(
      map(responses => {
        const flatResponse = responses.flat()

        if (!equalFnOrProp) {
          return flatResponse
        }

        if (typeof equalFnOrProp === 'function') {
          return uniqueByPredicate(flatResponse, equalFnOrProp)
        }

        return uniqueBy(flatResponse, equalFnOrProp)
      })
    )
  }

  /**
   * Determines the minimum number of batches to send the query parameters
   * The query parameters are sent in batches to avoid the URL length limit (2048 characters)
   * @param {string[]} values
   * @param {(values: string[]) => QueryParams | HttpParams} queryParamsFn
   * @param {number} [nBatches=1]
   * @returns {number}
   */
  private getQueryParamMinBatches(
    values: string[],
    queryParamsFn: (values: string[]) => QueryParams | HttpParams,
    nBatches = 1
  ): number {
    const valueChunk = divideArray(values, nBatches)[0] || []
    const params = queryParamsFn(valueChunk)

    const queryParams = params instanceof HttpParams ? params.toString() : JSON.stringify(params)
    const queryParamsLength = encodeURIComponent(queryParams).length

    if (queryParamsLength > this.maxQueryParamsLength) {
      return this.getQueryParamMinBatches(values, queryParamsFn, nBatches + 1)
    }

    return nBatches
  }

  /**
   * Sends a POST request to the API
   * @param {string} url
   * @param {unknown} body
   * @param {Options} options
   * @returns {Observable<T>}
   */
  post<T>(url: string, body: unknown, options?: Options): Observable<T> {
    return this.createMutationRequest(url, options, requestOptions => super.post<T>(url, body, requestOptions))
  }

  /**
   * Sends a PUT request to the API
   * @param {string} url
   * @param {unknown} body
   * @param {Options} options
   * @returns {Observable<T>}
   */
  put<T>(url: string, body: unknown, options?: Options): Observable<T> {
    return this.createMutationRequest(url, options, requestOptions => super.put<T>(url, body, requestOptions))
  }

  /**
   * Sends a DELETE request to the API
   * @param {string} url
   * @param {Options} options
   * @returns {Observable<T>}
   */
  delete<T>(url: string, options?: Options): Observable<T> {
    return this.createMutationRequest(url, options, requestOptions => super.delete<T>(url, requestOptions))
  }

  /**
   * Creates a mutation request and adds an optional pending W3C trace context per subscription.
   * @param {string} url
   * @param {Options} options
   * @param {(requestOptions: object) => Observable<T>} request
   * @returns {Observable<T>}
   */
  private createMutationRequest<T>(
    url: string,
    options: Options | undefined,
    request: (requestOptions: object) => Observable<T>
  ): Observable<T> {
    return defer(() => {
      const traceContext = options?.generateTraceId ? this.createSignalTraceContext() : undefined
      const requestOptions = traceContext ? this.addTraceParentHeader(options, traceContext.traceParent) : options

      if (traceContext) {
        this.registerPendingSignalTrace(traceContext.traceId, url, options?.timeout)
      }

      let request$ = request(this.getHttpOptions(requestOptions) as object).pipe(
        tap(response => this.saveSignalExclusionFromResponseTraceId(response, options)),
        map(response => this.unwrapTraceResponse<T>(response, options) as T),
        tap(() => {
          if (options?.invalidateCache) {
            this.apiCacheService.invalidate(options.invalidateCache)
          }
        })
      )

      request$ = this.applyMutationTimeout(request$, url, options?.timeout)

      if (!traceContext) {
        return request$
      }

      return request$.pipe(
        finalize(() => {
          queueMicrotask(() => this.settlePendingSignalTrace(traceContext.traceId))
        })
      )
    })
  }

  /**
   * Applies the existing client timeout behavior to one mutation request.
   * @param {Observable<T>} request$
   * @param {string} url
   * @param {number} timeout
   * @returns {Observable<T>}
   */
  private applyMutationTimeout<T>(request$: Observable<T>, url: string, timeout?: number): Observable<T> {
    if (this.hasUnlimitedMutationTimeout(url) || this.isTest) {
      return request$
    }

    return request$.pipe(
      timeoutWith(
        timeout ?? this.timeout,
        throwError(() => {
          this.showError('Client timeout', q.translations.errors.somethingWentWrong2)
          return new HttpResponse({ status: 503, statusText: 'Client timeout' })
        })
      )
    )
  }

  /**
   * Checks whether a mutation is allowed to run without the client timeout.
   * @param {string} url
   * @returns {boolean}
   */
  private hasUnlimitedMutationTimeout(url: string): boolean {
    return url.includes('/files') || url.includes('/invitations')
  }

  /**
   * Creates a valid W3C trace context with cryptographically random identifiers.
   * @returns {SignalTraceContext}
   */
  private createSignalTraceContext(): SignalTraceContext {
    const traceId = this.createRandomHexId(16)
    const spanId = this.createRandomHexId(8)

    return {
      traceId,
      traceParent: `00-${traceId}-${spanId}-00`
    }
  }

  /**
   * Creates a non-zero lowercase hexadecimal identifier.
   * @param {number} byteLength
   * @returns {string}
   */
  private createRandomHexId(byteLength: number): string {
    let bytes: Uint8Array

    do {
      bytes = crypto.getRandomValues(new Uint8Array(byteLength))
    } while (bytes.every(byte => byte === 0))

    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Adds traceparent without mutating the caller's headers.
   * @param {Options} options
   * @param {string} traceParent
   * @returns {Options}
   */
  private addTraceParentHeader(options: Options | undefined, traceParent: string): Options {
    const headers = options?.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options?.headers)

    return {
      ...options,
      headers: headers.set('traceparent', traceParent)
    }
  }

  /**
   * Registers a pending signal trace and releases the oldest trace when the registry is full.
   * @param {string} traceId
   * @param {string} url
   * @param {number} timeout
   */
  private registerPendingSignalTrace(traceId: string, url: string, timeout?: number): void {
    if (this.pendingSignalTraces.size >= this.maxPendingSignalTraces) {
      const oldestTraceId = this.pendingSignalTraces.keys().next().value

      this.settlePendingSignalTrace(oldestTraceId)
    }

    const expiryTimeout = this.hasUnlimitedMutationTimeout(url)
      ? undefined
      : window.setTimeout(
          () => this.settlePendingSignalTrace(traceId),
          (timeout ?? this.timeout) + this.signalTraceExpiryGrace
        )

    this.pendingSignalTraces.set(traceId, { expiryTimeout })
  }

  /**
   * Removes a pending trace and tells signal consumers to replay buffered events.
   * @param {string} traceId
   */
  private settlePendingSignalTrace(traceId?: string): void {
    if (!traceId) {
      return
    }

    const normalizedTraceId = traceId.toLowerCase()
    const pendingTrace = this.pendingSignalTraces.get(normalizedTraceId)

    if (!pendingTrace) {
      return
    }

    if (pendingTrace.expiryTimeout !== undefined) {
      clearTimeout(pendingTrace.expiryTimeout)
    }

    this.pendingSignalTraces.delete(normalizedTraceId)
    this.signalTraceSettledSubject.next(normalizedTraceId)
  }

  /**
   * Defines the retry policy of the requests sent to the API
   * @param {Options} options The original request options
   */
  private retryPolicy(options?: Options): void {
    if (this.shouldSkipRetryPolicy()) {
      return
    }

    const url = this.url

    this.req = defer(() => {
      const retryState = this.createRetryState(url, options)

      const request = this.executeRetryAttempt(retryState).pipe(
        catchError((error: HttpErrorResponse) => this.handleRetryError(error, retryState)),
        catchError((finalError: HttpErrorResponse) => this.handleFinalRetryError(finalError, retryState))
      )

      if (app !== 'mobile' || !retryState.workspaceId) {
        return request
      }

      // Cancel the whole retry sequence, including any waiting backoff timer.
      return request.pipe(
        takeUntil(this.workspaceChanges.pipe(filter(() => this.workspace()?.id !== retryState.workspaceId)))
      )
    })
  }

  /**
   * Checks if retry policy should be skipped for this request
   * @returns {boolean}
   */
  private shouldSkipRetryPolicy(): boolean {
    return this.url.includes('/v1/versions') || this.isTest
  }

  /**
   * Creates the state object for retry operations
   * @param {string} url The request URL
   * @param {Options} options The original request options
   * @returns {RetryState}
   */
  private createRetryState(url: string, options?: Options): RetryState {
    const startedAt = Date.now()

    return {
      workspaceId: app === 'mobile' ? this.workspace()?.id : undefined,
      url,
      options,
      requestTimeout: options?.timeout || this.timeout,
      shouldApplyTimeout: !url.includes('/files') && !url.includes('/invitations'),
      deadlineAt: this.isSyncRequest(url) ? startedAt + this.syncRetryTimeBudget : undefined,
      attempt: 0,
      lastErrorStatus: undefined
    }
  }

  /**
   * Executes a single retry attempt
   * @param {RetryState} state The retry state object
   * @returns {Observable<unknown>}
   */
  private executeRetryAttempt(state: RetryState): Observable<unknown> {
    // A retry timer can fire before the workspace-change effect runs.
    if (state.workspaceId && this.workspace()?.id !== state.workspaceId) {
      return EMPTY
    }

    const requestTimeout = this.getRequestTimeout(state)

    if (requestTimeout <= 0) {
      return throwError(
        () => new HttpErrorResponse({ status: 0, statusText: 'Client retry budget exhausted', url: state.url })
      )
    }

    state.attempt++

    const requestOptions = ApiClient.addAttemptHeader(state.options, state.attempt, state.lastErrorStatus)

    return this.makeRequestWithTimeout(state.url, requestOptions, requestTimeout, state.shouldApplyTimeout)
  }

  /**
   * Checks if an error is retryable (5XX or network error)
   * @param {HttpErrorResponse} error The error to check
   * @returns {boolean}
   */
  private isRetryableError(error: HttpErrorResponse): boolean {
    return error.status === 0 || (error.status >= 500 && error.status < 600)
  }

  /**
   * Gets the delay for the next retry when the request is still within its retry limits.
   * @param {RetryState} state The retry state
   * @returns {number | undefined}
   */
  private getNextRetryDelay(state: RetryState): number | undefined {
    if (state.attempt > this.getMaxRetries(state.url)) {
      return undefined
    }

    const retryDelay = this.getRetryDelay(state.attempt, state.url)

    if (state.deadlineAt !== undefined && Date.now() + retryDelay >= state.deadlineAt) {
      return undefined
    }

    return retryDelay
  }

  /**
   * Handles an error and potentially retries the request
   * @param {HttpErrorResponse} error The error to handle
   * @param {RetryState} state The retry state object
   * @returns {Observable<unknown>}
   */
  private handleRetryError(error: HttpErrorResponse, state: RetryState): Observable<unknown> {
    if (this.isRetryableError(error)) {
      const retryDelay = this.getNextRetryDelay(state)

      if (retryDelay === undefined) {
        return throwError(() => error)
      }

      state.lastErrorStatus = error.status

      return this.scheduleRetry(state, retryDelay)
    }

    return throwError(() => error)
  }

  /**
   * Schedules a retry attempt after a delay
   * @param {RetryState} state The retry state object
   * @param {number} retryDelay The delay before the next attempt
   * @returns {Observable<unknown>}
   */
  private scheduleRetry(state: RetryState, retryDelay: number): Observable<unknown> {
    return of(null).pipe(
      delay(retryDelay),
      switchMap(() => this.executeRetryAttempt(state)),
      catchError((error: HttpErrorResponse) => this.handleRetryError(error, state))
    )
  }

  /**
   * Calculates an exponential backoff delay for retries.
   * @param {number} attempt The current retry attempt (1-based)
   * @param {string} url The request URL
   * @returns {number}
   */
  private getRetryDelay(attempt: number, url: string): number {
    const retryDelay = this.delay * 2 ** (attempt - 1)

    if (!this.isSyncRequest(url)) {
      return retryDelay
    }

    const cappedRetryDelay = Math.min(retryDelay, this.maxSyncRetryDelay)

    return Math.round(cappedRetryDelay / 2 + Math.random() * (cappedRetryDelay / 2))
  }

  /**
   * Gets the timeout for the next request without allowing a sync request to exceed its deadline.
   * @param {RetryState} state The retry state
   * @returns {number}
   */
  private getRequestTimeout(state: RetryState): number {
    if (state.deadlineAt === undefined) {
      return state.requestTimeout
    }

    return Math.min(state.requestTimeout, Math.max(0, state.deadlineAt - Date.now()))
  }

  /**
   * Gets the maximum number of retries for a request URL.
   * @param {string} url The request URL
   * @returns {number}
   */
  private getMaxRetries(url: string): number {
    return this.isSyncRequest(url) ? this.syncRetries : this.retries
  }

  /**
   * Determines whether a request targets a sync endpoint.
   * @param {string} url The request URL
   * @returns {boolean}
   */
  private isSyncRequest(url: string): boolean {
    return /(^|\/)api\/v\d+\/sync\//.test(url)
  }

  /**
   * Handles exhausted retries, leaving feedback to callers that explicitly own recovery.
   * @param {HttpErrorResponse} error - The final error.
   * @param {RetryState} state - The retry state and caller options.
   * @returns {Observable<never>} The failed request.
   */
  private handleFinalRetryError(error: HttpErrorResponse, state: RetryState): Observable<never> {
    if (this.isRetryableError(error)) {
      // Keep network and server failures distinct for tracking, without showing a toast.
      if (state.url.includes('/me/supportToken') || state.options?.context?.get(LOCAL_ERROR_FEEDBACK)) {
        return throwError(() => error)
      }

      const retriesAttempted = Math.max(0, state.attempt - 1)

      this.showError(
        `Sorry, there was an timeout (after ${retriesAttempted} retries)`,
        q.translations.errors.somethingWentWrong2
      )

      return throwError(() => new HttpResponse({ status: 503, statusText: 'Server timeout' }))
    }

    return throwError(() => error)
  }

  /**
   * Makes a request with optional timeout handling
   * @param {string} url The URL to request
   * @param {Options} requestOptions The request options
   * @param {number} requestTimeout The timeout in milliseconds
   * @param {boolean} shouldApplyTimeout Whether to apply timeout
   * @returns {Observable<unknown>}
   */
  private makeRequestWithTimeout(
    url: string,
    requestOptions: Options,
    requestTimeout: number,
    shouldApplyTimeout: boolean
  ): Observable<unknown> {
    let req$ = super.get(url, this.getHttpOptions(requestOptions) as object).pipe(
      tap(response => this.saveSignalExclusionFromResponseTraceId(response, requestOptions)),
      map(response => this.unwrapTraceResponse(response, requestOptions))
    )

    if (shouldApplyTimeout) {
      req$ = req$.pipe(
        timeoutWith(
          requestTimeout,
          throwError(() => new HttpErrorResponse({ status: 0, statusText: 'Client timeout', url }))
        )
      )
    }

    return req$
  }

  /**
   * Removes ApiClient-only options before forwarding options to Angular's HttpClient.
   * @param {Options & { apiCache?: unknown; }} options
   * @returns {Options}
   */
  private getHttpOptions(options?: Options & { apiCache?: unknown }): Options {
    const {
      entityName,
      propertyName,
      generateTraceId: _generateTraceId,
      apiCache: _apiCache,
      invalidateCache: _invalidateCache,
      ...forwardedOptions
    } = options ?? {}

    if (
      entityName &&
      propertyName &&
      forwardedOptions.observe !== 'events' &&
      forwardedOptions.observe !== 'response'
    ) {
      return { ...forwardedOptions, observe: 'response' }
    }

    return forwardedOptions
  }

  /**
   * Keeps the public ApiClient response shape unchanged after trace headers were inspected.
   * @param {T | HttpResponse<T>} response
   * @param {Options} options
   * @returns {T | HttpResponse<T>}
   */
  private unwrapTraceResponse<T>(response: T | HttpResponse<T>, options?: Options): T | HttpResponse<T> {
    if (
      !options?.entityName ||
      !options.propertyName ||
      options.observe === 'events' ||
      options.observe === 'response'
    ) {
      return response
    }

    if (response instanceof HttpResponse) {
      return response.body
    }

    return response
  }

  /**
   * Stores a signal exclusion when a response contains a trace id.
   * @param {unknown} response
   * @param {Options} options
   */
  private saveSignalExclusionFromResponseTraceId(response: unknown, options?: Options): void {
    if (!options?.entityName || !options.propertyName || !(response instanceof HttpResponse)) {
      return
    }

    const traceId = response.headers.get('trace-id')

    if (traceId) {
      this.saveSignalExclusionForTraceId(traceId, {
        entityName: options.entityName,
        propertyName: options.propertyName
      })
    }
  }

  /**
   * Adds the X-Attempt and X-Retry-Reason headers to the request options
   * @param {Options | undefined} options The original request options
   * @param {number} attempt The attempt number (1, 2, 3, etc.)
   * @param {number} retryReason The HTTP status code that caused the retry
   * @returns {Options} The options with the retry headers added
   */
  static addAttemptHeader(options: Options | undefined, attempt: number, retryReason?: number): Options {
    const newOptions: Options = { ...(options || {}) }

    if (attempt > 1) {
      let headers = newOptions.headers instanceof HttpHeaders ? newOptions.headers : new HttpHeaders(newOptions.headers)

      headers = headers.set('X-Attempt', attempt.toString())

      if (retryReason !== undefined) {
        headers = headers.set('X-Retry-Reason', retryReason.toString())
      }

      newOptions.headers = headers
    }

    return newOptions
  }

  /**
   * Defines the client timeout policy of the request sent to the API
   * @param {number} timeout
   * @param {boolean} showError
   */
  private timeoutPolicy(timeout?: number, showError?: boolean): void {
    // Exclude files endpoints for the timeout policy
    if (this.url && !this.url.includes('/files') && !this.url.includes('/invitations') && !this.isTest) {
      this.req = this.req.pipe(
        timeoutWith(
          timeout ? timeout : this.timeout,
          throwError(() => {
            if (showError) {
              this.showError('Client timeout', q.translations.errors.somethingWentWrong2)
            }

            return new HttpResponse({ status: 503, statusText: 'Client timeout' })
          })
        )
      )
    }
  }

  /**
   * Shows an error after a failed request
   * @param {string} logMessage
   * @param {string} clientMessage
   * @private
   */
  private showError(logMessage: string, clientMessage: string): void {
    console.log(logMessage)
    this.clientError$.next(clientMessage)
  }

  /**
   * Retries a failed observable sequence based on the provided configuration.
   * @param {RetryConfig} [options={}]
   * @returns
   */
  genericRetryStrategy =
    ({ maxRetryAttempts = 3, scalingDuration = 1000, excludedStatusCodes = [] }: RetryConfig = {}) =>
    (attempts: Observable<HttpResponse<unknown>>) => {
      return attempts.pipe(
        mergeMap((error, i) => {
          const retryAttempt = i + 1

          // if maximum number of retries have been met
          // or response is a status code we don't wish to retry, throw error
          if (retryAttempt > maxRetryAttempts || excludedStatusCodes.find(e => e === error.status)) {
            return throwError(error)
          }

          // retry after exponential backoff: 1x, 2x, 4x, etc.
          return timer(scalingDuration * 2 ** (retryAttempt - 1))
        })
      )
    }
}

export interface QueryParams {
  page?: number
  pageSize?: number
  orderBy?: string
  filterBy?: string
  filterByC?: string
  count?: boolean
  select?: string
  showArchived?: boolean
}
